import { useState, useEffect } from "react";
import axios from "axios";
import { IoMdClose } from "react-icons/io";
import toast from "react-hot-toast";
import NotificationBadge from "../../components/NotificationBadge";

const CustomDropdown = ({
    label,
    value,
    options,
    onChange,
    isOpen,
    onToggle
}: {
    label: string;
    value: string;
    options: { label: string; value: string }[];
    onChange: (val: string) => void;
    isOpen: boolean;
    onToggle: () => void;
}) => {
    return (
        <div className="relative w-full lg:w-[32%]">
            <button
                onClick={(e) => { e.stopPropagation(); onToggle(); }}
                className={`w-full bg-[#DCEEE3] py-2.5 px-4 pr-10 text-[13px] text-gray-800 outline-none text-center font-medium transition-colors relative ${isOpen ? 'rounded-t-lg' : 'rounded-lg'}`}
            >
                {value === 'all' ? label : options.find(o => o.value === value)?.label || label}
                <svg className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                </svg>
            </button>
            {isOpen && (
                <div className="absolute top-full left-0 w-full bg-white shadow-[0_4px_12px_rgba(0,0,0,0.05)] z-20 py-2 rounded-b-lg border border-t-0 border-gray-100">
                    <ul className="max-h-60 overflow-y-auto">
                        {value !== 'all' && (
                            <li
                                className="py-2.5 px-4 text-[13px] text-gray-600 hover:bg-gray-50 hover:text-[#008060] cursor-pointer text-center"
                                onClick={(e) => { e.stopPropagation(); onChange('all'); onToggle(); }}
                            >
                                {label}
                            </li>
                        )}
                        {options.map((opt) => (
                            <li
                                key={opt.value}
                                className={`py-2.5 px-4 text-[13px] cursor-pointer text-center ${value === opt.value ? 'bg-gray-50 text-[#008060] font-medium' : 'text-gray-600 hover:bg-gray-50 hover:text-[#008060]'}`}
                                onClick={(e) => { e.stopPropagation(); onChange(opt.value); onToggle(); }}
                            >
                                {opt.label}
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
};

// Helper to extract role string from staff data (handles both new adminRole array and legacy role field)
const getStaffRole = (s: any): string => {
    if (Array.isArray(s.adminRole) && s.adminRole.length > 0) {
        return s.adminRole.map((r: any) => r.role || r).join(", ");
    }
    if (Array.isArray(s.role)) {
        return s.role.join(", ");
    }
    return typeof s.role === "string" ? s.role : "";
};

const StaffManagement = () => {
    const auth = sessionStorage.getItem("authState");
    const authState = auth && JSON.parse(auth);

    const [staff, setStaff] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState("");
    const [search, setSearch] = useState("");
    const [departmentFilter, setDepartmentFilter] = useState("all");
    const [roleFilter, setRoleFilter] = useState("all");
    const [statusFilter, setStatusFilter] = useState("all");
    const [selectedStaff, setSelectedStaff] = useState<string[]>([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [openDropdown, setOpenDropdown] = useState<string | null>(null);
    const itemsPerPage = 10;

    // Close dropdowns on outside click
    useEffect(() => {
        const handleClickOutside = () => setOpenDropdown(null);
        document.addEventListener("click", handleClickOutside);
        return () => document.removeEventListener("click", handleClickOutside);
    }, []);

    // Add Staff Modal
    const [showAddStaffModal, setShowAddStaffModal] = useState(false);
    const [newStaff, setNewStaff] = useState({
        firstName: "",
        lastName: "",
        personalEmail: "",
        officialEmail: "",
        personalPhone: "",
        officialPhone: "",
        department: "",
        role: "account-officer",
    });
    const [addStaffLoading, setAddStaffLoading] = useState(false);

    // View Staff Modal
    const [showViewModal, setShowViewModal] = useState(false);
    const [viewStaffData, setViewStaffData] = useState<any>(null);
    const [viewLoading, setViewLoading] = useState(false);
    const [showDeactivateModal, setShowDeactivateModal] = useState(false);
    const [deactivateLoading, setDeactivateLoading] = useState(false);

    const fetchStaffById = async (staffId: string) => {
        setViewLoading(true);
        setShowViewModal(true);
        setViewStaffData(null);
        try {
            const auth: any = sessionStorage.getItem("authState");
            const jsonData = JSON.parse(auth || "{}");
            const token = jsonData?.token;

            const res = await axios.get(
                `${process.env.REACT_APP_API_URL}/staffs/${staffId}`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );
            setViewStaffData(res.data?.data || null);
        } catch (err: any) {
            console.error("Error fetching staff details:", err);
            toast.error(err.response?.data?.message || "Failed to fetch staff details");
            setShowViewModal(false);
        } finally {
            setViewLoading(false);
        }
    };

    useEffect(() => {
        fetchStaff();
    }, []);

    const fetchStaff = async () => {
        setIsLoading(true);
        setError("");
        try {
            const auth: any = sessionStorage.getItem("authState");
            const jsonData = JSON.parse(auth || "{}");
            const token = jsonData?.token;

            // Fetch first page to get total count
            const firstPage = await axios.get(
                `${process.env.REACT_APP_API_URL}/staffs?page=1&limit=100`,
                {
                    headers: {
                        Authorization: token,
                    },
                }
            );

            const responseData = firstPage.data;
            setStaff(responseData?.data || []);
        } catch (err: any) {
            console.error("Error fetching staff:", err);
            setError(err.response?.data?.message || err.message || "Failed to fetch staff");
        } finally {
            setIsLoading(false);
        }
    };

    // Extract unique departments and roles from API data only
    const departments = Array.from(new Set(
        staff.map((s: any) => s.department).filter(Boolean)
    )).map((d: any) => typeof d === "string" ? d : String(d));

    const roles = Array.from(new Set(
        staff.flatMap((s: any) => {
            const role = getStaffRole(s);
            return role ? role.split(", ") : [];
        }).filter(Boolean)
    )).map((r: any) => typeof r === "string" ? r : String(r));

    // Filter logic
    const filteredStaff = staff.filter((s: any) => {
        const staffName = s.fullName || s.name || `${s.firstName || ""} ${s.lastName || ""}`.trim();
        const matchesSearch = !search ||
            staffName.toLowerCase().includes(search.toLowerCase()) ||
            s.email?.toLowerCase().includes(search.toLowerCase()) ||
            s.phoneNumber?.includes(search);
        const matchesDepartment = departmentFilter === "all" || s.department === departmentFilter;
        const staffRole = getStaffRole(s);
        const matchesRole = roleFilter === "all" || staffRole.includes(roleFilter);
        const matchesStatus = statusFilter === "all" ||
            (statusFilter === "Active" ? s.status !== "inactive" : s.status === "inactive");
        return matchesSearch && matchesDepartment && matchesRole && matchesStatus;
    });

    // Pagination
    const totalPages = Math.ceil(filteredStaff.length / itemsPerPage);
    const paginatedStaff = filteredStaff.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    const toggleSelectStaff = (id: string) => {
        setSelectedStaff(prev =>
            prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]
        );
    };

    const toggleSelectAll = () => {
        if (selectedStaff.length === paginatedStaff.length && paginatedStaff.length > 0) {
            setSelectedStaff([]);
        } else {
            setSelectedStaff(paginatedStaff.map((s: any) => s._id || s.staffId));
        }
    };

    const handleAddStaff = async () => {
        if (!newStaff.firstName || !newStaff.lastName || !newStaff.personalEmail || !newStaff.personalPhone) return;
        setAddStaffLoading(true);
        try {
            const auth: any = sessionStorage.getItem("authState");
            const jsonData = JSON.parse(auth || "{}");
            const token = jsonData?.token;

            await axios.post(
                `${process.env.REACT_APP_API_URL}/staffs`,
                {
                    firstName: newStaff.firstName.trim(),
                    lastName: newStaff.lastName.trim(),
                    official_email: newStaff.personalEmail,
                    email: newStaff.officialEmail,
                    phoneNumber: newStaff.personalPhone,
                    department: newStaff.department,
                    adminRole: [{ role: newStaff.role }],
                },
                {
                    headers: {
                        Authorization: token,
                    },
                }
            );
            setShowAddStaffModal(false);
            setNewStaff({ firstName: "", lastName: "", personalEmail: "", officialEmail: "", personalPhone: "", officialPhone: "", department: "", role: "account-officer" });
            fetchStaff();
            toast.success("Staff added successfully!");
        } catch (err: any) {
            console.error("Error adding staff:", err);
            toast.error(err.response?.data?.message || "Failed to add staff");
        } finally {
            setAddStaffLoading(false);
        }
    };

    const handleExportAll = () => {
        const headers = ["S/N", "Name", "Email", "Phone Number", "Department", "Role", "Status"];
        const csvRows = [headers.join(",")];
        filteredStaff.forEach((s: any, idx: number) => {
            csvRows.push([
                idx + 1,
                s.name || "N/A",
                s.officialEmail || "",
                s.phoneNumber || "",
                s.department || "N/A",
                getStaffRole(s).replace(/[-_]/g, " ") || "N/A",
                s.status === "inactive" ? "Inactive" : "Active",
            ].join(","));
        });
        const blob = new Blob([csvRows.join("\n")], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = "staff_export.csv";
        link.click();
        URL.revokeObjectURL(url);
    };

    return (
        <main className="w-full">
            {/* Header */}
            <div className="mb-8">
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Staff Management</h1>
                <p className="text-gray-500 text-xs sm:text-sm">Manage all staff members and their roles</p>
            </div>

            {showViewModal && viewLoading ? (
                <div className="animate-in fade-in duration-200">
                    <div className="flex justify-end mb-4">
                        <button
                            onClick={() => { setShowViewModal(false); setViewStaffData(null); }}
                            className="text-sm font-semibold text-gray-800 hover:text-black transition-colors"
                        >
                            Back
                        </button>
                    </div>
                    <div className="flex items-center justify-center py-32">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#008060]"></div>
                        <span className="ml-3 text-sm text-gray-500">Loading staff details...</span>
                    </div>
                </div>
            ) : showViewModal && viewStaffData ? (
                <div className="animate-in fade-in duration-200">
                    {/* Header Controls */}
                    <div className="flex justify-end mb-4">
                        <button
                            onClick={() => { setShowViewModal(false); setViewStaffData(null); }}
                            className="text-sm font-semibold text-gray-800 hover:text-black transition-colors"
                        >
                            Back
                        </button>
                    </div>

                    {/* Profile Header Card */}
                    <div className="bg-white rounded-[20px] border border-gray-100 shadow-[0_2px_12px_rgba(0,0,0,0.03)] p-6 mb-6 relative overflow-hidden flex items-center gap-6">
                        {/* Green left accent */}
                        <div className="absolute left-0 top-0 bottom-0 w-2.5 bg-[#008060]"></div>

                        {/* Avatar */}
                        <div className="w-[76px] h-[76px] rounded-full shrink-0 p-[3px]" style={{ background: 'linear-gradient(135deg, #f9ce34, #ee2a7b, #6228d7)' }}>
                            <div className="w-full h-full rounded-full bg-white p-[3px]">
                                <div className="w-full h-full rounded-full bg-gray-200 flex items-center justify-center">
                                    <span className="text-xl font-bold text-gray-500">
                                        {(viewStaffData.fullName || viewStaffData.name || `${viewStaffData.firstName || ""} ${viewStaffData.lastName || ""}`.trim())
                                            .split(" ").map((n: string) => n.charAt(0).toUpperCase()).slice(0, 2).join("") || "N/A"}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Info & Actions */}
                        <div className="flex-1 flex flex-col md:flex-row md:items-center justify-between gap-4">
                            <div>
                                <div className="flex items-center gap-3 mb-1.5">
                                    <h2 className="text-lg font-bold text-gray-900 tracking-tight">
                                        {viewStaffData.fullName || viewStaffData.name || `${viewStaffData.firstName || ""} ${viewStaffData.lastName || ""}`.trim() || "N/A"}
                                    </h2>
                                    <span className={`px-2.5 py-1 rounded-md text-[11px] font-bold ${viewStaffData.status === "active"
                                        ? "bg-[#DCEEE3] text-[#008060]"
                                        : "bg-red-50 text-red-600"
                                        }`}>
                                        {viewStaffData.status === "active" ? "Active" : "Inactive"}
                                    </span>
                                </div>
                                <p className="text-[13px] text-gray-400 font-medium capitalize">
                                    {getStaffRole(viewStaffData).replace(/[-_]/g, " ") || "N/A"}
                                </p>
                            </div>

                            <div className="flex items-center gap-8 pr-4">
                                <button className="text-[13px] font-bold text-[#008060] hover:text-[#006048] transition-colors">
                                    Edit Information
                                </button>
                                <button
                                    onClick={() => setShowDeactivateModal(true)}
                                    className={`text-[13px] font-bold transition-colors ${viewStaffData.status === "inactive"
                                        ? "text-[#008060] hover:text-[#006048]"
                                        : "text-red-500 hover:text-red-700"
                                        }`}
                                >
                                    {viewStaffData.status === "inactive" ? "Activate" : "Deactivate"}
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Main Content Grid */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* Left Column */}
                        <div className="space-y-6">
                            {/* Personal Details */}
                            <div className="bg-white rounded-[20px] border border-gray-100 shadow-[0_2px_12px_rgba(0,0,0,0.03)] px-8 py-7">
                                <h3 className="text-[14px] font-bold text-[#008060] mb-8">Personal Details</h3>
                                <div className="grid grid-cols-2 gap-y-10 gap-x-6">
                                    <div>
                                        <div className="flex items-center gap-2 text-gray-400 mb-2.5">
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
                                            <span className="text-[11px] font-semibold">First Name</span>
                                        </div>
                                        <p className="text-[13px] font-bold text-gray-800">{viewStaffData.firstName || "N/A"}</p>
                                    </div>
                                    <div>
                                        <div className="flex items-center gap-2 text-gray-400 mb-2.5">
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
                                            <span className="text-[11px] font-semibold">Last Name</span>
                                        </div>
                                        <p className="text-[13px] font-bold text-gray-800">{viewStaffData.lastName || "N/A"}</p>
                                    </div>
                                    <div>
                                        <div className="flex items-center gap-2 text-gray-400 mb-2.5">
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                                            <span className="text-[11px] font-semibold">Email Address</span>
                                        </div>
                                        <p className="text-[13px] font-bold text-gray-800 break-words">{viewStaffData.email || "N/A"}</p>
                                    </div>
                                    <div>
                                        <div className="flex items-center gap-2 text-gray-400 mb-2.5">
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>
                                            <span className="text-[11px] font-semibold">Phone Number</span>
                                        </div>
                                        <p className="text-[13px] font-bold text-gray-800">{viewStaffData.phoneNumber || "N/A"}</p>
                                    </div>
                                    <div>
                                        <div className="flex items-center gap-2 text-gray-400 mb-2.5">
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                                            <span className="text-[11px] font-semibold">Region</span>
                                        </div>
                                        <p className="text-[13px] font-bold text-gray-800">{viewStaffData.region || "N/A"}</p>
                                    </div>
                                    <div>
                                        <div className="flex items-center gap-2 text-gray-400 mb-2.5">
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" /></svg>
                                            <span className="text-[11px] font-semibold">Referral Code</span>
                                        </div>
                                        <p className="text-[13px] font-bold text-[#008060] font-mono tracking-wider">{viewStaffData.referralCode || "N/A"}</p>
                                    </div>
                                </div>
                            </div>

                            {/* Work Information */}
                            <div className="bg-white rounded-[20px] border border-gray-100 shadow-[0_2px_12px_rgba(0,0,0,0.03)] p-8">
                                <h3 className="text-[14px] font-bold text-gray-800 tracking-tight">Work Information</h3>
                                <p className="text-[11px] font-medium text-gray-400 mb-8 mt-1.5">Role and department details</p>
                                <div className="grid grid-cols-2 gap-y-6 gap-x-4">
                                    <div>
                                        <h4 className="text-[11px] font-medium text-gray-400 mb-2.5">Department</h4>
                                        <p className="text-[13px] font-bold text-gray-800 capitalize">{typeof viewStaffData.department === "string" ? viewStaffData.department.replace(/_/g, " ") : (viewStaffData.department || "N/A")}</p>
                                    </div>
                                    <div>
                                        <h4 className="text-[11px] font-medium text-gray-400 mb-2.5">Role</h4>
                                        <p className="text-[13px] font-bold text-gray-800 capitalize">
                                            {getStaffRole(viewStaffData).replace(/[-_]/g, " ") || "N/A"}
                                        </p>
                                    </div>
                                    <div>
                                        <h4 className="text-[11px] font-medium text-gray-400 mb-2.5">Status</h4>
                                        <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${viewStaffData.status === "active" ? "bg-[#e6f5ee] text-[#008060]" : "bg-red-50 text-red-500"}`}>
                                            {viewStaffData.status === "active" ? "Active" : "Inactive"}
                                        </span>
                                    </div>
                                    <div>
                                        <h4 className="text-[11px] font-medium text-gray-400 mb-2.5">Assigned Customers</h4>
                                        <p className="text-[13px] font-bold text-gray-800">{viewStaffData.assignedCustomers?.length || 0}</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Right Column - Assigned Customers */}
                        <div className="bg-white rounded-[20px] border border-gray-100 shadow-[0_2px_12px_rgba(0,0,0,0.03)] p-8 h-full min-h-[400px] flex flex-col">
                            <h3 className="text-[14px] font-bold text-gray-800 tracking-tight">Assigned Customers</h3>
                            <p className="text-[11px] font-medium text-gray-400 mb-6 mt-1.5">Customers assigned to this staff member ({viewStaffData.assignedCustomers?.length || 0})</p>

                            {viewStaffData.assignedCustomers?.length > 0 ? (
                                <div className="flex-1 space-y-3 overflow-y-auto max-h-[420px] pr-1">
                                    {viewStaffData.assignedCustomers.map((customer: any, idx: number) => (
                                        <div key={customer.id || idx} className="flex items-center justify-between p-3.5 rounded-xl border border-gray-100 hover:border-[#008060]/20 hover:bg-[#008060]/[0.02] transition-all">
                                            <div className="flex items-center gap-3">
                                                <div className="w-9 h-9 rounded-full bg-[#008060]/10 flex items-center justify-center text-[#008060] text-xs font-bold">
                                                    {customer.name ? customer.name.split(" ").map((n: string) => n.charAt(0).toUpperCase()).slice(0, 2).join("") : "?"}
                                                </div>
                                                <div>
                                                    <p className="text-sm font-semibold text-gray-800">{customer.name || "N/A"}</p>
                                                    <p className="text-xs text-gray-400 mt-0.5">{customer.email || customer.phoneNumber || ""}</p>
                                                </div>
                                            </div>
                                            <p className="text-[10px] text-gray-400 whitespace-nowrap">
                                                {customer.assignedDate ? new Date(customer.assignedDate).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : ""}
                                            </p>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="flex-1 flex items-center justify-center">
                                    <p className="text-[11px] font-bold text-gray-500">No customers assigned yet</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            ) : (
                <section className="bg-white rounded-[20px] p-6 shadow-sm border border-gray-100">
                    <div className="flex flex-col gap-4 mb-6">
                        {/* Top row: Search & Buttons */}
                        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                            <div className="flex-1 max-w-4xl">
                                <input
                                    type="text"
                                    placeholder="Search by Name, E-mail..."
                                    value={search}
                                    onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
                                    className="w-full bg-gray-50 border border-gray-100 rounded-lg py-3 px-4 text-sm focus:ring-1 focus:ring-gray-200 outline-none placeholder-gray-400"
                                />
                            </div>
                            <div className="flex items-center gap-3 flex-wrap">
                                <button
                                    onClick={handleExportAll}
                                    className="bg-[#008060] text-white px-8 py-2.5 rounded-lg text-sm font-medium hover:bg-[#006048] transition-colors"
                                >
                                    Export all
                                </button>
                                <button
                                    onClick={() => setShowAddStaffModal(true)}
                                    className="bg-[#008060] text-white px-8 py-2.5 rounded-lg text-sm font-medium hover:bg-[#006048] transition-colors"
                                >
                                    +Add New Staff
                                </button>
                            </div>
                        </div>

                        {/* Bottom row: Filters */}
                        <div className="flex flex-col lg:flex-row items-center justify-between gap-6 w-full">
                            <CustomDropdown
                                label="All Departments"
                                value={departmentFilter}
                                options={departments.map((d: any) => ({ label: d.replace(/[-_]/g, " ").replace(/\b\w/g, (c: string) => c.toUpperCase()), value: d }))}
                                onChange={(val) => { setDepartmentFilter(val); setCurrentPage(1); }}
                                isOpen={openDropdown === 'department'}
                                onToggle={() => setOpenDropdown(prev => prev === 'department' ? null : 'department')}
                            />
                            <CustomDropdown
                                label="All Roles"
                                value={roleFilter}
                                options={roles.map((r: any) => ({ label: r.replace(/[-_]/g, " ").replace(/\b\w/g, (c: string) => c.toUpperCase()), value: r }))}
                                onChange={(val) => { setRoleFilter(val); setCurrentPage(1); }}
                                isOpen={openDropdown === 'role'}
                                onToggle={() => setOpenDropdown(prev => prev === 'role' ? null : 'role')}
                            />
                            <CustomDropdown
                                label="All Status"
                                value={statusFilter}
                                options={[
                                    { label: "Active Only", value: "Active" },
                                    { label: "Inactive Only", value: "Inactive" }
                                ]}
                                onChange={(val) => { setStatusFilter(val); setCurrentPage(1); }}
                                isOpen={openDropdown === 'status'}
                                onToggle={() => setOpenDropdown(prev => prev === 'status' ? null : 'status')}
                            />
                        </div>
                    </div>

                    {/* Results Count */}
                    <p className="text-sm text-gray-500 mb-4">
                        Showing {paginatedStaff.length} out of {filteredStaff.length} Staff
                    </p>

                    {/* Table */}
                    {isLoading ? (
                        <div className="flex items-center justify-center py-16">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#008060]"></div>
                            <span className="ml-3 text-sm text-gray-500">Loading staff...</span>
                        </div>
                    ) : error ? (
                        <div className="text-center py-16">
                            <p className="text-sm text-red-500 mb-2">{error}</p>
                            <button
                                onClick={fetchStaff}
                                className="text-sm text-[#008060] underline hover:text-[#006048]"
                            >
                                Retry
                            </button>
                        </div>
                    ) : filteredStaff.length === 0 ? (
                        <div className="text-center py-16">
                            <p className="text-sm text-gray-400">No staff members found</p>
                        </div>
                    ) : (
                        <>
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm text-left">
                                    <thead className="bg-transparent text-gray-800 font-medium border-b border-gray-100">
                                        <tr>
                                            <th className="py-4 px-4 w-12">
                                                <button onClick={toggleSelectAll} className="focus:outline-none flex items-center">
                                                    {selectedStaff.length === paginatedStaff.length && paginatedStaff.length > 0 ? (
                                                        <img src="/assets/material-symbols-light_select-check-box.svg" alt="checked" className="w-6 h-6" />
                                                    ) : (
                                                        <div className="w-6 h-6 border-2 border-gray-300 rounded flex items-center justify-center hover:border-gray-400 transition-colors"></div>
                                                    )}
                                                </button>
                                            </th>
                                            <th className="py-4 px-4 w-12">S/N</th>
                                            <th className="py-4 px-4">Name</th>
                                            <th className="py-4 px-4">Official E-mail</th>
                                            <th className="py-4 px-4">Phone Number</th>
                                            <th className="py-4 px-4">Department</th>
                                            <th className="py-4 px-4">Role</th>
                                            <th className="py-4 px-4">Status</th>
                                            <th className="py-4 px-4">Action</th>
                                        </tr>
                                    </thead>
                                    <tbody className="text-gray-500">
                                        {paginatedStaff.map((s: any, idx: number) => {
                                            const staffId = s._id || s.staffId || s.id;
                                            const isActive = s.status !== "inactive";
                                            return (
                                                <tr key={staffId || idx} className="border-b border-gray-50 hover:bg-gray-50/50 transition-colors">
                                                    <td className="py-4 px-4">
                                                        <button onClick={() => toggleSelectStaff(staffId)} className="focus:outline-none flex items-center">
                                                            {selectedStaff.includes(staffId) ? (
                                                                <img src="/assets/material-symbols-light_select-check-box.svg" alt="checked" className="w-6 h-6" />
                                                            ) : (
                                                                <div className="w-6 h-6 border-2 border-gray-300 rounded flex items-center justify-center hover:border-gray-400 transition-colors"></div>
                                                            )}
                                                        </button>
                                                    </td>
                                                    <td className="py-4 px-4 text-gray-400 font-mono text-xs">{(currentPage - 1) * itemsPerPage + idx + 1}</td>
                                                    <td className="py-4 px-4 text-gray-800 font-medium">
                                                        {s.name || "N/A"}
                                                    </td>
                                                    <td className="py-4 px-4">{s.officialEmail || "N/A"}</td>
                                                    <td className="py-4 px-4">{s.phoneNumber || "N/A"}</td>
                                                    <td className="py-4 px-4">{s.department?.replace("_", " ") || "N/A"}</td>
                                                    <td className="py-4 px-4 capitalize">
                                                        {getStaffRole(s).replace(/[-_]/g, " ") || "N/A"}
                                                    </td>
                                                    <td className="py-4 px-4">
                                                        <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${isActive ? "bg-[#e6f5ee] text-[#008060]" : "bg-gray-100 text-gray-500"}`}>
                                                            {isActive ? "Active" : "Inactive"}
                                                        </span>
                                                    </td>
                                                    <td className="py-4 px-4">
                                                        <div className="flex items-center gap-2">
                                                            <button
                                                                onClick={() => fetchStaffById(staffId)}
                                                                className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors"
                                                                title="View"
                                                            >
                                                                <img src="/assets/proicons_eye.svg" alt="view" className="w-5 h-5" />
                                                            </button>
                                                            {/* <button
                                                                className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors"
                                                                title="Edit"
                                                            >
                                                                <img src="/assets/material-symbols-light_edit-outline-rounded.svg" alt="edit" className="w-5 h-5" />
                                                            </button>
                                                            <button
                                                                className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-red-50 transition-colors"
                                                                title="Delete"
                                                            >
                                                                <img src="/assets/material-symbols-light_delete-outline.svg" alt="delete" className="w-5 h-5" />
                                                            </button> */}
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>

                            {/* Pagination */}
                            <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-100">
                                <p className="text-xs text-gray-400">Page {currentPage} of {totalPages}</p>
                                <div className="flex gap-2">
                                    <button
                                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                        disabled={currentPage === 1}
                                        className="px-4 py-1.5 border rounded text-xs font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                                    >
                                        Previous
                                    </button>
                                    <button
                                        onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                        disabled={currentPage >= totalPages}
                                        className="px-4 py-1.5 border rounded text-xs font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                                    >
                                        Next
                                    </button>
                                </div>
                            </div>
                        </>
                    )}
                </section>
            )}

            {/* Add Staff Modal */}
            {showAddStaffModal && (
                <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl w-full max-w-[460px] p-8 shadow-2xl animate-in fade-in zoom-in duration-200 overflow-y-auto max-h-[95vh] [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
                        <div className="flex items-center justify-between mb-1">
                            <h3 className="text-[17px] font-bold text-gray-900">Add New Staff Member</h3>
                            <button onClick={() => setShowAddStaffModal(false)} className="text-gray-400 hover:text-gray-600 transition-colors">
                                <IoMdClose size={22} />
                            </button>
                        </div>
                        <p className="text-[#8c949e] text-[13px] mb-8">Enter the details of the new staff member to add them to the system</p>

                        <div className="space-y-5 mb-8">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-[13px] font-semibold text-gray-800 mb-2">First Name</label>
                                    <input
                                        type="text"
                                        value={newStaff.firstName}
                                        onChange={(e) => setNewStaff({ ...newStaff, firstName: e.target.value })}
                                        placeholder="Enter First Name"
                                        className="w-full bg-[#fcfcfd] border border-gray-100/80 rounded-lg py-3 px-4 text-[13px] text-gray-800 focus:ring-1 focus:ring-[#008060] outline-none placeholder-gray-400"
                                    />
                                </div>
                                <div>
                                    <label className="block text-[13px] font-semibold text-gray-800 mb-2">Last Name</label>
                                    <input
                                        type="text"
                                        value={newStaff.lastName}
                                        onChange={(e) => setNewStaff({ ...newStaff, lastName: e.target.value })}
                                        placeholder="Enter Last Name"
                                        className="w-full bg-[#fcfcfd] border border-gray-100/80 rounded-lg py-3 px-4 text-[13px] text-gray-800 focus:ring-1 focus:ring-[#008060] outline-none placeholder-gray-400"
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="block text-[13px] font-semibold text-gray-800 mb-2">Personal E-mail</label>
                                <input
                                    type="email"
                                    value={newStaff.personalEmail}
                                    onChange={(e) => setNewStaff({ ...newStaff, personalEmail: e.target.value })}
                                    placeholder="Personal@email"
                                    className="w-full bg-[#fcfcfd] border border-gray-100/80 rounded-lg py-3 px-4 text-[13px] text-gray-800 focus:ring-1 focus:ring-[#008060] outline-none placeholder-gray-400"
                                />
                            </div>
                            <div>
                                <label className="block text-[13px] font-semibold text-gray-800 mb-2">Official E-mail</label>
                                <input
                                    type="email"
                                    value={newStaff.officialEmail}
                                    onChange={(e) => setNewStaff({ ...newStaff, officialEmail: e.target.value })}
                                    placeholder="Employee@sovereigntech"
                                    className="w-full bg-[#fcfcfd] border border-gray-100/80 rounded-lg py-3 px-4 text-[13px] text-gray-800 focus:ring-1 focus:ring-[#008060] outline-none placeholder-gray-400"
                                />
                            </div>
                            <div>
                                <label className="block text-[13px] font-semibold text-gray-800 mb-2">Personal Phone Number</label>
                                <input
                                    type="tel"
                                    value={newStaff.personalPhone}
                                    onChange={(e) => setNewStaff({ ...newStaff, personalPhone: e.target.value })}
                                    placeholder="Enter Full Name" // Matching mockup literally
                                    className="w-full bg-[#fcfcfd] border border-gray-100/80 rounded-lg py-3 px-4 text-[13px] text-gray-800 focus:ring-1 focus:ring-[#008060] outline-none placeholder-gray-400"
                                />
                            </div>
                            <div>
                                <label className="block text-[13px] font-semibold text-gray-800 mb-2">Official Phone Number</label>
                                <input
                                    type="tel"
                                    value={newStaff.officialPhone}
                                    onChange={(e) => setNewStaff({ ...newStaff, officialPhone: e.target.value })}
                                    placeholder="Active" // Matching mockup literally
                                    className="w-full bg-[#fcfcfd] border border-gray-100/80 rounded-lg py-3 px-4 text-[13px] text-gray-800 focus:ring-1 focus:ring-[#008060] outline-none placeholder-gray-400"
                                />
                            </div>
                            <div>
                                <label className="block text-[13px] font-semibold text-gray-800 mb-2">Department</label>
                                <div className="relative">
                                    <select
                                        value={newStaff.department}
                                        onChange={(e) => setNewStaff({ ...newStaff, department: e.target.value })}
                                        className="w-full appearance-none bg-[#fcfcfd] border border-gray-100/80 rounded-lg py-3 px-4 text-[13px] text-gray-600 outline-none focus:ring-1 focus:ring-[#008060] cursor-pointer"
                                    >
                                        <option value="">E.g Sales</option>
                                        <option value="account officer">Account Officer</option>
                                        <option value="Sales">Sales</option>
                                        <option value="Operations">Operations</option>
                                        <option value="Administration">Administration</option>
                                        <option value="Customer Service">Customer Service</option>
                                    </select>
                                    <div className="absolute inset-y-0 right-0 flex items-center px-4 pointer-events-none text-gray-400">
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                                        </svg>
                                    </div>
                                </div>
                            </div>
                            <div>
                                <label className="block text-[13px] font-semibold text-gray-800 mb-2">Roles</label>
                                <div className="relative mb-1.5">
                                    <select
                                        value={newStaff.role}
                                        onChange={(e) => setNewStaff({ ...newStaff, role: e.target.value })}
                                        className="w-full appearance-none bg-[#fcfcfd] border border-gray-100/80 rounded-lg py-3 px-4 text-[13px] text-gray-600 outline-none focus:ring-1 focus:ring-[#008060] cursor-pointer"
                                    >
                                        <option value="account-officer">Account Officer</option>
                                        <option value="admin">Admin</option>
                                        <option value="superadmin">Super Admin</option>
                                        <option value="designer">Designer</option>
                                        <option value="frontend-dev">Front End Dev</option>
                                        <option value="marketer">Marketer</option>
                                    </select>
                                    <div className="absolute inset-y-0 right-0 flex items-center px-4 pointer-events-none text-gray-400">
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                                        </svg>
                                    </div>
                                </div>
                            </div>

                            {newStaff.role === 'account-officer' && (
                                <p className="text-[#008060] text-[10px] font-medium mt-1.5 px-1">
                                    *A unique referral code will be automatically generated for this Account Officer
                                </p>
                            )}
                        </div>

                        <div className="flex items-center gap-4 mt-8">
                            <button
                                onClick={() => setShowAddStaffModal(false)}
                                className="flex-1 py-3 border border-gray-100 shadow-sm rounded-lg text-sm font-medium text-gray-500 hover:bg-gray-50 transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleAddStaff}
                                disabled={addStaffLoading || !newStaff.firstName || !newStaff.lastName || !newStaff.personalEmail || !newStaff.personalPhone}
                                className="flex-[1.2] py-3 bg-[#008060] rounded-lg text-sm font-medium text-white hover:bg-[#006048] transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-[0_4px_12px_rgba(0,128,96,0.15)]"
                            >
                                {addStaffLoading ? "Adding..." : "Add Staff Member"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Activate / Deactivate Confirmation Modal */}
            {showDeactivateModal && viewStaffData && (() => {
                const isCurrentlyActive = viewStaffData.status === "active";
                return (
                    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setShowDeactivateModal(false)}>
                        <div
                            className="bg-white rounded-2xl w-full max-w-[440px] shadow-2xl animate-in fade-in zoom-in duration-200 overflow-hidden"
                            onClick={(e) => e.stopPropagation()}
                        >
                            {/* Header */}
                            <div className="px-7 pt-6 pb-4 flex items-start justify-between">
                                <h3 className={`text-lg font-bold ${isCurrentlyActive ? 'text-red-500' : 'text-[#008060]'}`}>
                                    {isCurrentlyActive ? 'Deactivate' : 'Activate'} Staff Member?
                                </h3>
                                <button
                                    onClick={() => setShowDeactivateModal(false)}
                                    className="text-gray-400 hover:text-gray-600 transition-colors mt-0.5"
                                >
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
                                </button>
                            </div>

                            {/* Body */}
                            <div className="px-7 pb-6">
                                <p className="text-sm text-gray-600 mb-5">
                                    Are you sure you want to {isCurrentlyActive ? 'deactivate' : 'activate'} <strong>{viewStaffData.fullName || viewStaffData.name || "this staff member"}</strong>?
                                </p>

                                {/* Info Box */}
                                <div className={`${isCurrentlyActive ? 'bg-red-50 border-red-100' : 'bg-green-50 border-green-100'} border rounded-xl p-5`}>
                                    <p className="text-xs font-bold text-gray-700 mb-3">
                                        Note: This Account Officer will:
                                    </p>
                                    <ul className="space-y-2.5">
                                        {isCurrentlyActive ? (
                                            <>
                                                <li className="flex items-start gap-2 text-xs text-gray-600">
                                                    <span className="w-1.5 h-1.5 rounded-full bg-gray-400 mt-1 shrink-0"></span>
                                                    No longer be able to log in
                                                </li>
                                                <li className="flex items-start gap-2 text-xs text-gray-600">
                                                    <span className="w-1.5 h-1.5 rounded-full bg-gray-400 mt-1 shrink-0"></span>
                                                    Have their referral code <strong className="text-gray-800">{viewStaffData.referralCode ? `(${viewStaffData.referralCode})` : ""}</strong> invalidated
                                                </li>
                                                <li className="flex items-start gap-2 text-xs text-gray-600">
                                                    <span className="w-1.5 h-1.5 rounded-full bg-gray-400 mt-1 shrink-0"></span>
                                                    Be removed from assignment lists
                                                </li>
                                                <li className="flex items-start gap-2 text-xs text-gray-600">
                                                    <span className="w-1.5 h-1.5 rounded-full bg-gray-400 mt-1 shrink-0"></span>
                                                    Keep existing customer assignments for historical tracking
                                                </li>
                                            </>
                                        ) : (
                                            <>
                                                <li className="flex items-start gap-2 text-xs text-gray-600">
                                                    <span className="w-1.5 h-1.5 rounded-full bg-[#008060] mt-1 shrink-0"></span>
                                                    Be able to log in again
                                                </li>
                                                <li className="flex items-start gap-2 text-xs text-gray-600">
                                                    <span className="w-1.5 h-1.5 rounded-full bg-[#008060] mt-1 shrink-0"></span>
                                                    Have their referral code <strong className="text-gray-800">{viewStaffData.referralCode ? `(${viewStaffData.referralCode})` : ""}</strong> reactivated
                                                </li>
                                                <li className="flex items-start gap-2 text-xs text-gray-600">
                                                    <span className="w-1.5 h-1.5 rounded-full bg-[#008060] mt-1 shrink-0"></span>
                                                    Be available for new customer assignments
                                                </li>
                                                <li className="flex items-start gap-2 text-xs text-gray-600">
                                                    <span className="w-1.5 h-1.5 rounded-full bg-[#008060] mt-1 shrink-0"></span>
                                                    Retain all previous customer assignment history
                                                </li>
                                            </>
                                        )}
                                    </ul>
                                </div>
                            </div>

                            {/* Footer */}
                            <div className="px-7 pb-6 flex items-center gap-4">
                                <button
                                    onClick={() => setShowDeactivateModal(false)}
                                    className="flex-1 py-3 border border-gray-200 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={async () => {
                                        setDeactivateLoading(true);
                                        const newStatus = isCurrentlyActive ? "inactive" : "active";
                                        try {
                                            const auth: any = sessionStorage.getItem("authState");
                                            const jsonData = JSON.parse(auth || "{}");
                                            const token = jsonData?.token;
                                            await axios.put(
                                                `${process.env.REACT_APP_API_URL}/staffs/${viewStaffData.id || viewStaffData._id}`,
                                                { status: newStatus },
                                                { headers: { Authorization: `Bearer ${token}` } }
                                            );
                                            toast.success(`${viewStaffData.fullName || viewStaffData.name || "Staff member"} has been ${isCurrentlyActive ? 'deactivated' : 'activated'}`);
                                            setShowDeactivateModal(false);
                                            setShowViewModal(false);
                                            setViewStaffData(null);
                                            fetchStaff();
                                        } catch (err: any) {
                                            toast.error(err.response?.data?.message || `Failed to ${isCurrentlyActive ? 'deactivate' : 'activate'} staff member`);
                                        } finally {
                                            setDeactivateLoading(false);
                                        }
                                    }}
                                    disabled={deactivateLoading}
                                    className={`flex-1 py-3 rounded-xl text-sm font-bold text-white transition-colors disabled:opacity-50 ${isCurrentlyActive
                                        ? 'bg-red-500 hover:bg-red-600'
                                        : 'bg-[#008060] hover:bg-[#006048]'
                                        }`}
                                >
                                    {deactivateLoading
                                        ? (isCurrentlyActive ? "Deactivating..." : "Activating...")
                                        : (isCurrentlyActive ? "Deactivate" : "Activate")
                                    }
                                </button>
                            </div>
                        </div>
                    </div>
                );
            })()}
        </main>
    );
};

export default StaffManagement;
